import type { KnowledgeMap } from '@reados/contracts';

export interface KnowledgeMapViewport { x: number; y: number; scale: number }
export interface KnowledgeMapMenu { nodeID: string; normalizedX: number; normalizedY: number }
export interface KnowledgeMapRenderNode {
  content: string;
  payload: { id: string; title: string; pages: string; pageNumber: number | null; answerCount: number; isSyntheticRoot: boolean; isSelected: boolean };
  children: KnowledgeMapRenderNode[];
}
export interface KnowledgeMapPayload { mapID: string; documentPageCount: number; root: KnowledgeMapRenderNode }

const escaped = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Mirrors ReadOSKnowledgeMapRuntime.swift's RenderPayload and escaped node HTML. */
export function projectKnowledgeMap(map: KnowledgeMap, pageNumbersByNodeId: Record<string, number> = {}, documentPageCount = 0, selectedNodeId?: string): KnowledgeMapPayload {
  const nodes = new Map<string, KnowledgeMapRenderNode>();
  for (const node of map.nodes) {
    if (!node.id || nodes.has(node.id)) throw new Error('知识地图包含重复或缺失的节点标识');
    const page = pageNumbersByNodeId[node.id];
    const pageNumber = Number.isInteger(page) && page >= 1 && (!documentPageCount || page <= documentPageCount) ? page : null;
    const answerCount = node.answers?.length || 0;
    const content = `<span class="km-node"><span class="km-title">${escaped(node.title)}</span>`
      + (node.pages ? `<span class="km-page">${escaped(node.pages)}</span>` : '')
      + (answerCount ? `<span class="km-answer-badge" role="button" aria-label="查看 ${answerCount} 条已存讲解">${answerCount}</span>` : '') + '</span>';
    nodes.set(node.id, { content, payload: { id: node.id, title: node.title, pages: node.pages, pageNumber, answerCount, isSyntheticRoot: false, isSelected: node.id === selectedNodeId }, children: [] });
  }
  const roots: KnowledgeMapRenderNode[] = [];
  for (const node of map.nodes) {
    const rendered = nodes.get(node.id)!;
    if (!node.parentId) roots.push(rendered);
    else {
      const parent = nodes.get(node.parentId);
      if (!parent) throw new Error('知识地图包含无法找到的父节点');
      parent.children.push(rendered);
    }
  }
  // A flat persisted map can contain malformed cycles. Reject them before
  // recursive Markmap traversal, including cycles disconnected from the roots.
  const visited = new Set<KnowledgeMapRenderNode>();
  const pending = [...roots];
  while (pending.length) {
    const node = pending.pop()!;
    if (visited.has(node)) throw new Error('知识地图包含循环节点');
    visited.add(node);
    pending.push(...node.children);
  }
  if (visited.size !== nodes.size) throw new Error('知识地图包含循环节点');
  return {
    mapID: map.id,
    documentPageCount,
    root: roots.length === 1 ? roots[0] : {
      content: `<span class="km-node"><span class="km-title km-root-title">${escaped(map.title)}</span></span>`,
      payload: { id: map.id, title: map.title, pages: '', pageNumber: null, answerCount: 0, isSyntheticRoot: true, isSelected: false },
      children: roots,
    },
  };
}

export function validKnowledgeMapViewport(value: unknown): value is KnowledgeMapViewport {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return typeof v.x === 'number' && Number.isFinite(v.x) && typeof v.y === 'number' && Number.isFinite(v.y)
    && typeof v.scale === 'number' && Number.isFinite(v.scale) && v.scale >= 0.02 && v.scale <= 80;
}

export type KnowledgeMapEvent = { type: 'ready' } | { type: 'rendered' } | { type: 'error'; message: string }
  | { type: 'action'; mapID: string; action: 'jump' | 'showAnswers'; nodeID: string }
  | ({ type: 'action'; mapID: string; action: 'showMenu' } & KnowledgeMapMenu)
  | ({ type: 'action'; mapID: string; action: 'viewport' } & KnowledgeMapViewport);

/** Called only after checking the exact iframe Window and its opaque origin. */
export function parseKnowledgeMapEvent(value: unknown, token: string): KnowledgeMapEvent | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (v.channel !== 'reados-knowledge-map-v1' || v.token !== token) return null;
  if (v.type === 'ready' || v.type === 'rendered') return { type: v.type };
  if (v.type === 'error' && typeof v.message === 'string') return { type: 'error', message: v.message.slice(0, 1000) };
  if (v.type !== 'action' || typeof v.mapID !== 'string') return null;
  if (v.action === 'viewport' && validKnowledgeMapViewport(v)) return { type: 'action', action: 'viewport', mapID: v.mapID, x: v.x, y: v.y, scale: v.scale };
  if (typeof v.nodeID !== 'string' || !v.nodeID || v.nodeID.length > 500) return null;
  if (v.action === 'jump' || v.action === 'showAnswers') return { type: 'action', action: v.action, mapID: v.mapID, nodeID: v.nodeID };
  if (v.action === 'showMenu' && typeof v.normalizedX === 'number' && typeof v.normalizedY === 'number'
      && Number.isFinite(v.normalizedX) && v.normalizedX >= 0 && v.normalizedX <= 1
      && Number.isFinite(v.normalizedY) && v.normalizedY >= 0 && v.normalizedY <= 1) {
    return { type: 'action', action: 'showMenu', mapID: v.mapID, nodeID: v.nodeID, normalizedX: v.normalizedX, normalizedY: v.normalizedY };
  }
  return null;
}
